/**
 * P03 — Primo accesso: cambio password forzato.
 *
 * L'utente è autenticato (con la password temporanea generata da
 * `createStaffUser` / `onboardPatient`) e ha `mustChangePasswordAtNextLogin
 * = true`. Lo costringiamo a impostare una password robusta prima di
 * accedere al resto dell'app.
 *
 * Flusso:
 * 1. Re-auth client-side con vecchia password (`reauthenticateWithCredential`)
 * 2. `updatePassword` client-side (unico modo di cambiare password con
 *    email/password provider)
 * 3. Cloud Function `changePasswordAfterFirstLogin` per:
 *    - audit log
 *    - `mustChangePasswordAtNextLogin = false`
 *    - `revokeRefreshTokens` (forza il refresh del token al prossimo
 *      idToken request)
 * 4. `refreshToken()` client-side
 * 5. Redirect: staff non enrollato → /mfa/enroll, altrimenti /app
 */

import { forwardRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../security/firebase';
import { useAuth } from '../../hooks/useAuth';
import { isStaffRole } from '@shared/types/auth';

const STRONG_PASSWORD = z
  .string()
  .min(12, 'Almeno 12 caratteri')
  .regex(/[a-z]/, 'Deve contenere una lettera minuscola')
  .regex(/[A-Z]/, 'Deve contenere una lettera maiuscola')
  .regex(/[0-9]/, 'Deve contenere una cifra')
  .regex(/[^A-Za-z0-9]/, 'Deve contenere un simbolo');

const schema = z
  .object({
    oldPassword: z.string().min(1, 'Inserisci la password attuale'),
    newPassword: STRONG_PASSWORD,
    confirm: z.string().min(1, 'Conferma la nuova password'),
  })
  .refine((data) => data.newPassword === data.confirm, {
    path: ['confirm'],
    message: 'Le password non coincidono',
  })
  .refine((data) => data.newPassword !== data.oldPassword, {
    path: ['newPassword'],
    message: 'La nuova password deve essere diversa dalla precedente',
  });

type Input = z.infer<typeof schema>;

export function FirstLoginPage() {
  const navigate = useNavigate();
  const { role, mfaEnrolled, refreshToken } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [genericError, setGenericError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Input>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: Input) => {
    setGenericError(null);
    setSubmitting(true);
    try {
      const u = auth.currentUser;
      if (!u || !u.email) {
        throw new Error('not-authenticated');
      }
      const credential = EmailAuthProvider.credential(u.email, data.oldPassword);
      await reauthenticateWithCredential(u, credential);
      await updatePassword(u, data.newPassword);
      const fn = httpsCallable<unknown, { ok: true }>(
        functions,
        'changePasswordAfterFirstLogin'
      );
      await fn({});
      await refreshToken();
      if (isStaffRole(role) && !mfaEnrolled) {
        navigate('/mfa/enroll', { replace: true });
      } else {
        navigate('/app', { replace: true });
      }
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setGenericError('Password attuale non corretta.');
      } else {
        setGenericError(
          'Non è stato possibile aggiornare la password. Riprova.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-app flex items-center justify-center p-6">
      <div className="w-full max-w-[480px] bg-white rounded-xl shadow-sm p-8 md:p-12">
        <header className="mb-8">
          <h1 className="font-headline-lg text-headline-lg text-text-main mb-2">
            Imposta una nuova password
          </h1>
          <p className="font-body-md text-text-secondary">
            Per la sicurezza dell'account, scegli una password robusta
            prima di continuare.
          </p>
        </header>

        {genericError && (
          <div
            role="alert"
            className="mb-6 p-3 rounded-lg bg-error-container text-on-error-container text-body-sm"
          >
            {genericError}
          </div>
        )}

        <form
          noValidate
          className="space-y-5"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Field
            id="oldPassword"
            label="Password attuale"
            type="password"
            autoComplete="current-password"
            error={errors.oldPassword?.message}
            {...register('oldPassword')}
          />
          <Field
            id="newPassword"
            label="Nuova password"
            type="password"
            autoComplete="new-password"
            error={errors.newPassword?.message}
            helper="Almeno 12 caratteri, con minuscole, maiuscole, cifra e simbolo."
            {...register('newPassword')}
          />
          <Field
            id="confirm"
            label="Conferma nuova password"
            type="password"
            autoComplete="new-password"
            error={errors.confirm?.message}
            {...register('confirm')}
          />

          <button
            type="submit"
            disabled={submitting}
            className="w-full h-12 bg-primary text-on-primary font-body-md font-bold rounded-lg shadow-sm hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50"
          >
            {submitting ? 'Aggiornamento in corso…' : 'Imposta password'}
          </button>
        </form>
      </div>
    </div>
  );
}

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  helper?: string;
}

const Field = forwardRef<HTMLInputElement, FieldProps>(
  ({ label, error, helper, id, ...rest }, ref) => (
    <div>
      <label
        className="block font-label-sm text-on-surface-variant mb-2"
        htmlFor={id}
      >
        {label}
      </label>
      <input
        {...rest}
        id={id}
        ref={ref}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full h-12 px-4 rounded-lg border border-border-gray bg-white text-on-surface focus:outline-none focus:border-primary focus:ring-2 focus:ring-magenta-soft transition-all font-body-md"
      />
      {helper && !error && (
        <p className="mt-1 text-body-sm text-text-secondary">{helper}</p>
      )}
      {error && (
        <p className="mt-1 text-body-sm text-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
);
Field.displayName = 'Field';

export default FirstLoginPage;
